/**
 * Drilldown Tab Header Standard — design tokens.
 *
 * The literal values from the standard. Import `T` and reference it from `sx`
 * rather than re-typing hexes and sizes, so a change to the standard is a change
 * to this file.
 *
 * Everything here is in raw px on purpose. MUI's spacing multiplier (×8) can't
 * express the standard's scale (3 / 9 / 13 / 14 / 15 / 18 / 20), and rounding it
 * into eighths is how a fixed rhythm quietly drifts.
 */
import { teal } from "../../../../theme/colors";

export const TAB_TOKENS = {
  color: {
    // Surfaces
    bg: "#f4f6f9", // page background — the header sits directly on this
    surface: "#ffffff", // card fill
    surfaceSunk: "#fafbfc", // field wells, nested tiles

    // Lines
    border: "#e3e8ef", // card borders, dividers
    borderStrong: "#cdd5e0", // ghost button borders, frame edges

    // Text
    ink: "#0f172a", // titles, values
    ink2: "#475569", // body copy, ghost button labels
    ink3: "#64748b", // subtitles
    muted: "#94a3b8", // uppercase labels, counts

    // Action — the Eastview color system's teal ramp (src/theme/colors.js).
    // The brand teal is only 3.0:1 under white, so anything carrying a white
    // label is teal-600, not the teal-500 surface step.
    accent: teal[600], // primary button fill
    brand: teal[600], // active tab, links, AI accents
    brandSoft: teal[100], // brand-tinted backgrounds
    accentInk: teal[700], // darker brand — pressed / hover text on brand-soft

    // Status — soft fill + line, always used as a pair
    dangerSoft: "#fef2f2",
    dangerLine: "#fecaca",
    dangerInk: "#b91c1c",
    warnSoft: "#fffbeb",
    warnLine: "#fde68a",
    warnInk: "#b45309",
    okSoft: "#f0fdf4",
    okLine: "#bbf7d0",
    okInk: "#047857",
  },

  radius: {
    card: "10px",
    sm: "8px", // tiles, field wells
    xs: "7px", // buttons
  },

  shadow: {
    card: "0 1px 2px rgba(15,23,42,.04), 0 1px 3px rgba(15,23,42,.06)",
    pinned: "0 1px 3px rgba(15,23,42,.05)",
  },

  /** Every value is a multiple of 2. Do not introduce an off-scale number. */
  space: {
    // Root inset, owned by LoanTabCanvas. These are the Properties / Applicants
    // values (`py={4.5} px={1}` on the shared container) rather than the written
    // spec's flat 20px — those tabs are the reference for the gap between the tab
    // switcher and the first element, so the standard matches them.
    canvasPy: "36px",
    canvasPx: "8px",
    canvasPyTight: "20px", // < 560px
    canvasPxTight: "14px",
    titleGap: "3px", // page title → subtitle
    headerGap: "18px", // page header → next element (fixed; never varies)
    cardGap: "14px", // peer card → peer card
    cardPad: "15px 16px", // card body
    cardHeaderPad: "13px 16px", // section header row — slightly tighter
    kpiGap: "9px", // between KPI tiles
    controlGap: "16px", // section title → its controls (minimum)
    actionGap: "8px", // between header buttons
  },

  /** The two-tier type scale. Sentence case everywhere except the uppercase labels. */
  type: {
    pageTitle: {
      fontSize: "21px",
      fontWeight: 700,
      letterSpacing: "-0.02em",
      lineHeight: 1.25,
      color: "#0f172a",
    },
    pageSubtitle: {
      fontSize: "12.5px",
      fontWeight: 400,
      lineHeight: 1.45,
      color: "#64748b",
    },
    sectionTitle: {
      fontSize: "13.5px",
      fontWeight: 650,
      letterSpacing: "-0.01em",
      lineHeight: 1.3,
      color: "#0f172a",
    },
    sectionSubtitle: {
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: 1.45,
      color: "#64748b",
    },
    kpiLabel: {
      fontSize: "8.5px",
      fontWeight: 700,
      letterSpacing: "0.075em",
      lineHeight: 1.2,
      textTransform: "uppercase",
      color: "#94a3b8",
    },
    kpiValue: {
      fontSize: "15px",
      fontWeight: 650,
      lineHeight: 1.2,
      color: "#0f172a",
      fontVariantNumeric: "tabular-nums",
    },
    fieldLabel: {
      fontSize: "8.5px",
      fontWeight: 700,
      letterSpacing: "0.07em",
      lineHeight: 1.2,
      textTransform: "uppercase",
      color: "#94a3b8",
    },
    fieldValue: {
      fontSize: "13px",
      fontWeight: 600,
      lineHeight: 1.3,
      color: "#0f172a",
      fontVariantNumeric: "tabular-nums",
    },
    meta: {
      fontSize: "11.5px",
      fontWeight: 400,
      lineHeight: 1.4,
      color: "#94a3b8",
    },
  },

  font: {
    sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    // Loan IDs, reference numbers, file hashes. NOT currency — tabular-nums on
    // the sans face reads better for figures.
    mono: "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  },

  /** Raw breakpoints from §10. Used as max-width media queries. */
  bp: {
    kpiReflow: "@media (max-width: 1079.95px)",
    controlsWrap: "@media (max-width: 859.95px)",
    headerStack: "@media (max-width: 719.95px)",
    twoUp: "@media (max-width: 559.95px)",
  },
};

/** Status tone → { fill, line, ink }. `null`/"neutral" means no status treatment. */
export const TONE_STYLES = {
  neutral: {
    fill: TAB_TOKENS.color.surface,
    line: TAB_TOKENS.color.border,
    ink: TAB_TOKENS.color.ink,
  },
  danger: {
    fill: TAB_TOKENS.color.dangerSoft,
    line: TAB_TOKENS.color.dangerLine,
    ink: TAB_TOKENS.color.dangerInk,
  },
  warn: {
    fill: TAB_TOKENS.color.warnSoft,
    line: TAB_TOKENS.color.warnLine,
    ink: TAB_TOKENS.color.warnInk,
  },
  ok: {
    fill: TAB_TOKENS.color.okSoft,
    line: TAB_TOKENS.color.okLine,
    ink: TAB_TOKENS.color.okInk,
  },
  brand: {
    fill: TAB_TOKENS.color.brandSoft,
    line: "#c7d7fe",
    ink: TAB_TOKENS.color.brand,
  },
};

/**
 * The standard's empty-value rule: missing data renders as an em dash, never as
 * `0`, `N/A` or blank. `$0` and "no data" are different facts and must look
 * different — so this only substitutes for null/undefined/"".
 */
export const EM_DASH = "—";
export const orDash = (v) => (v === null || v === undefined || v === "" ? EM_DASH : v);

export default TAB_TOKENS;
